import { showToast } from './Toast';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Eye, X, ZoomIn } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';

// ── Lightbox تمام‌صفحه ────────────────────────────────────────────────────
const ImageLightbox = ({ src, alt, onClose }) => {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/40 rounded-full p-2 transition-colors"
      >
        <X className="w-6 h-6" />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

// ── کارت محصول ───────────────────────────────────────────────────────────
export const ProductCard = ({ product, showAddToCart = true }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist, isLoading: wishlistLoading } = useWishlist();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [modalType, setModalType]   = useState('');
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const isFavorite = isInWishlist(product.id);

  const handleAddToCart = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const user = localStorage.getItem('user');
    if (!user) { setModalType('cart'); setShowLoginModal(true); return; }
    setIsAddingToCart(true);
    // داده محصول رو مستقیم پاس میدیم تا نیازی به fetch دوباره نباشه
    const result = await addToCart(product.id, product);
    setIsAddingToCart(false);
    showToast(result?.success ? '✅ محصول به سبد خرید اضافه شد!' : '❌ ' + (result?.message || 'خطا در افزودن'));
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const result = await toggleWishlist(product);
    if (result?.needsLogin) { setModalType('wishlist'); setShowLoginModal(true); }
  };

  const handleImageClick = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (product.image) setLightboxSrc(product.image);
  };

  // ── مودال لاگین ───────────────────────────────────────────────────────
  const LoginModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={() => setShowLoginModal(false)}>
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {modalType === 'cart' ? <ShoppingCart className="w-8 h-8 text-amber-600" /> : <Heart className="w-8 h-8 text-amber-600" />}
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">ورود به حساب کاربری</h3>
          <p className="text-gray-600">
            {modalType === 'cart' ? 'برای افزودن به سبد خرید ابتدا وارد شوید' : 'برای افزودن به علاقه‌مندی‌ها ابتدا وارد شوید'}
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { setShowLoginModal(false); navigate('/auth'); }}
            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-lg font-semibold">
            ورود / ثبت‌نام
          </button>
          <button onClick={() => setShowLoginModal(false)}
            className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold">
            انصراف
          </button>
        </div>
      </div>
    </div>
  );

  const hasDiscount  = (product.discount > 0 || product.discount_percent > 0);
  const finalPrice   = Number(product.price || product.discounted_price || product.current_price || 0);
  const origPrice    = Number(product.originalPrice || product.price || 0);
  const displayOrig  = hasDiscount && origPrice > finalPrice ? origPrice : null;

  return (
    <>
      {showLoginModal && <LoginModal />}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} alt={product.name} onClose={() => setLightboxSrc(null)} />}

      <Link to={`/product/${product.id}`}
        className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-1 block group">

        {/* تصویر */}
        <div className="relative h-52 bg-gradient-to-br from-amber-50 to-yellow-50 overflow-hidden">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => { e.target.parentElement.classList.add('image-error'); e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-yellow-100">
              <span className="text-5xl opacity-40">🪙</span>
              <span className="absolute bottom-2 text-xs text-amber-400">بدون تصویر</span>
            </div>
          )}

          {/* بج تخفیف */}
          {hasDiscount && (
            <span className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
              {product.discount || product.discount_percent}% تخفیف
            </span>
          )}

          {/* دکمه علاقه‌مندی */}
          <button onClick={handleToggleWishlist} disabled={wishlistLoading}
            className={`absolute top-2 left-2 p-2 rounded-full backdrop-blur-sm transition-all
              ${isFavorite ? 'bg-red-500 text-white shadow-lg' : 'bg-white/80 text-gray-600 hover:bg-white'}
              ${wishlistLoading ? 'opacity-50 cursor-wait' : ''}`}>
            <Heart className={`w-4 h-4 ${isFavorite ? 'fill-white' : ''}`} />
          </button>

          {/* overlay مشاهده جزئیات */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            {product.image && (
              <button
                onClick={handleImageClick}
                className="bg-white/90 text-gray-800 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1 hover:bg-white transition-colors"
              >
                <ZoomIn className="w-4 h-4" />
                عکس کامل
              </button>
            )}
            <span className="bg-amber-500 text-white px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1">
              <Eye className="w-4 h-4" />
              جزئیات
            </span>
          </div>
        </div>

        {/* اطلاعات */}
        <div className="p-4">
          <h3 className="font-bold text-base mb-1 text-gray-800 truncate">{product.name}</h3>

          <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
            {product.category && <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{product.category}</span>}
            <span>وزن: {product.weight}گ</span>
            <span>•</span>
            <span>عیار: {product.karat}</span>
          </div>

          {/* قیمت */}
          <div className="mb-3">
            {displayOrig ? (
              <div>
                <span className="text-xs text-gray-400 line-through block">
                  {displayOrig.toLocaleString()} تومان
                </span>
                <span className="text-lg font-bold text-amber-600">
                  {finalPrice.toLocaleString()} تومان
                </span>
              </div>
            ) : (
              <span className="text-lg font-bold text-amber-600">
                {finalPrice > 0 ? `${finalPrice.toLocaleString()} تومان` : 'قیمت در حال محاسبه'}
              </span>
            )}
          </div>

          {/* موجودی */}
          {product.stock > 0 && product.stock <= 3 && (
            <p className="text-xs text-red-500 font-semibold mb-2">⚠️ فقط {product.stock} عدد باقی مانده</p>
          )}
          {(product.stock === 0 || product.inStock === false) && (
            <p className="text-xs text-gray-400 font-semibold mb-2 text-center">ناموجود</p>
          )}

          {/* دکمه سبد */}
          {showAddToCart && (
            <button onClick={handleAddToCart} disabled={isAddingToCart || product.stock === 0}
              className="w-full bg-gradient-to-r from-amber-400 to-orange-400 text-white py-2 rounded-lg font-semibold
                hover:from-amber-500 hover:to-orange-500 transition-all flex items-center justify-center gap-2
                disabled:opacity-50 disabled:cursor-not-allowed text-sm">
              {isAddingToCart
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> در حال افزودن...</>
                : <><ShoppingCart className="w-4 h-4" /> افزودن به سبد</>}
            </button>
          )}
        </div>
      </Link>
    </>
  );
};

export default ProductCard;






